/**
 * Lightweight client tracker — fire-and-forget POSTs to /api/analytics/track.
 *
 * Zero PII by default; if `lead_capture` is fired with an email, the server
 * hashes it (SHA-256) before persistence. Errors are swallowed silently so
 * tracking never blocks the UX.
 *
 * Session id is stored in sessionStorage so SPA navigations stay attributed
 * to one visit. Always pair calls with `usePersonalization()` so the event
 * is enriched with referrer/visitor_type/device/province.
 */
import { getAttribution, newEventId } from "./attribution";
const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const SESSION_KEY = "cr_track_session";

export function analyticsSessionId() {
  if (typeof sessionStorage === "undefined") return "anon";
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

export function track(event_type, props = {}) {
  if (!API) return;
  try {
    const attrib = getAttribution();
    const payload = {
      event_type,
      page_path: (typeof location !== "undefined" ? location.pathname : "/"),
      session_id: analyticsSessionId(),
      event_id: newEventId(),
      team: attrib.team || "",
      rep_id: attrib.rep_id || "",
      // Auto-attach ad/UTM attribution to every event so the rollup
      // dashboard can group by campaign without per-caller plumbing.
      utm_source:   attrib.utm_source   || "",
      utm_medium:   attrib.utm_medium   || "",
      utm_campaign: attrib.utm_campaign || "",
      utm_term:     attrib.utm_term     || "",
      utm_content:  attrib.utm_content  || "",
      gclid:        attrib.gclid        || "",
      fbclid:       attrib.fbclid       || "",
      ...props,
    };
    // Prefer sendBeacon when available — works on tab close.
    const url = `${API}/api/analytics/track`;
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const ok = navigator.sendBeacon(url, new Blob([JSON.stringify(payload)], { type: "application/json" }));
      if (ok) return;
    }
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => { /* noop */ });
  } catch { /* noop */ }
}

/**
 * Helper that bundles the most common signals from a usePersonalization()
 * snapshot so callers don't repeat the same prop spread on every track.
 */
export function trackWithPersonalization(event_type, personal, extra = {}) {
  track(event_type, {
    referrer: personal?.referrer || "direct",
    visitor_type: personal?.visitor || "first_time",
    device: personal?.device || "desktop",
    province: personal?.province?.code || "",
    ...extra,
  });
}
